import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  Menu,
  MenuItem,
  OutlinedInput,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getTenantAction } from "../../Actions/TenantActions";
import {
  exportReportDataAction,
  getTransactionHistoryByAmountChartAction,
  getTransactionHistoryByStatusChartAction,
  getTransactionHistoryDailyChartAction,
} from "../../Actions/TransactionActions";
import {
  DataGrid_SX,
  SelectDropdown_SX,
} from "../../Assets/customTheme/constants";
import CoustomTooltip from "../../Assets/customTheme/customtooltip";
import { TableReset } from "../../Assets/svg/svgicon";
import { formatCurrency } from "../../utils/CommonFunctions";
import CustomSnackbar from "../Common/snackBar";
import NoDataFound from "../Dashboard/NoDataFound";
import MonthlyTransactionReportModel from "./MonthlyTransactionReport";
import renderStatusBadge from "../../utils/badges";
import { MyExportIcon } from "../../Assets/svg/MyIcons";
import DateRangePicker from "../Common/DateRangePicker";
import { Bar, Doughnut } from "react-chartjs-2";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 1);
  return { from: start, to: end };
};

const TransactionReportModel = () => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [chartAmountData, setChartAmountData] = useState([]);
  const [chartStatusData, setChartStatusData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAmount, setLoadingAmount] = useState(false);
  const [isGlobalFilterModified, setIsGlobalFilterModified] = useState(false);
  const [isAmountFilterModified, setIsAmountFilterModified] = useState(false);
  const [isStatusFilterModified, setIsStatusFilterModified] = useState(false);

  // Memoize defaultRange to prevent recreation on every render
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  
  const [globalSelectedRange, setGlobalSelectedRange] = useState(defaultRange);
  const [globalStartDate, setGlobalStartDate] = useState(defaultRange.from.toISOString());
  const [globalEndDate, setGlobalEndDate] = useState(defaultRange.to.toISOString());

  const [selectedRange, setSelectedRange] = useState(defaultRange);
  const [startDate, setStartDate] = useState(defaultRange.from.toISOString());
  const [endDate, setEndDate] = useState(defaultRange.to.toISOString());

  const [selectedRangeForStatus, setSelectedRangeForStatus] = useState(defaultRange);
  const [startDateForStatus, setStartDateForStatus] = useState(defaultRange.from.toISOString());
  const [endDateForStatus, setEndDateForStatus] = useState(defaultRange.to.toISOString());

  const [merchantList, setMerchantList] = useState([]);
  const [globalSelectedMerchant, setGlobalSelectedMerchant] = useState("");
  const [selectedMerchant, setSelectedMerchant] = useState("");
  const [selectedMerchantForStatus, setSelectedMerchantForStatus] = useState("");
  const [globalSelectedWebsite, setGlobalSelectedWebsite] = useState("");
  
  // Export States
  const [exportLoader, setExportLoader] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const [monthlyData, setMonthlyData] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const areDatesEqual = useCallback((range1, range2) => {
    if (!range1 || !range2) return false;
    return (
      range1.from?.getTime() === range2.from?.getTime() &&
      range1.to?.getTime() === range2.to?.getTime()
    );
  }, []);

  const getAllWebsitesData = useCallback(async (merchantId) => {
    if (merchantId && merchantId !== "All merchants") {
      try {
        setGlobalSelectedWebsite("");
      } catch (error) {
        console.error("Error fetching website names:", error);
        setSnackbar({
          open: true,
          message:
            error?.response?.data?.message || "Error fetching website data",
          severity: "error",
        });
      }
    } else {
      setGlobalSelectedWebsite("");
    }
  }, []);

  const fetchTransactionData = useCallback(async (startDateParam = null, endDateParam = null, merchantId = "") => {
    const defaultStartDate = startDateParam ? new Date(startDateParam) : getDefaultDateRange().from;
    const defaultEndDate = endDateParam ? new Date(endDateParam) : getDefaultDateRange().to;

    setLoading(true);
    try {
      const params = {
        startDate: defaultStartDate.toISOString(),
        endDate: defaultEndDate.toISOString(),
        merchantId: merchantId,
      };

      const res = await getTransactionHistoryDailyChartAction(params);
      const transactions = res?.data;

      const monthlyFormattedData = transactions?.map((t) => ({
        date: t.transactionDate,
        totalTransactions: t.totalTransactions,
        totalAmount: parseFloat(t.totalAmount) || 0,
      }));

      setMonthlyData(monthlyFormattedData);
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Something went wrong. Please try again later.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const getMerchantListData = useCallback(async () => {
    try {
      const res = await getTenantAction(undefined, undefined, undefined, undefined, "1");
      const { data } = res;
      setMerchantList(data.data);
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Something went wrong. Please try again later.",
        severity: "error",
      });
    }
  }, []);

  const getTransactionHistoryByAmountData = useCallback(async (
    month,
    year,
    merchantId,
    websiteId
  ) => {
    setLoadingAmount(true);
    try {
      const res = await getTransactionHistoryByAmountChartAction(
        month,
        year,
        merchantId,
        websiteId || ""
      );
      const { data } = res;
      setChartAmountData(data);
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Something went wrong. Please try again later.",
        severity: "error",
      });
    } finally {
      setLoadingAmount(false);
    }
  }, []);

  const getTransactionHistoryByStatusData = useCallback(async (
    month,
    year,
    merchantId,
    websiteId
  ) => {
    setLoading(true);
    try {
      const res = await getTransactionHistoryByStatusChartAction(
        month,
        year,
        merchantId,
        websiteId || ""
      );
      const { data } = res;
      setChartStatusData(data);
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Something went wrong. Please try again later.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getAllWebsitesData(selectedMerchant);
  }, [selectedMerchant, getAllWebsitesData]);

  useEffect(() => {
    const isMerchantModified = globalSelectedMerchant !== "";
    setIsGlobalFilterModified(isMerchantModified || !areDatesEqual(globalSelectedRange, defaultRange));
  }, [globalSelectedRange, globalSelectedMerchant, defaultRange, areDatesEqual]);

  useEffect(() => {
    const isMerchantModified = selectedMerchant !== "";
    setIsAmountFilterModified(isMerchantModified || !areDatesEqual(selectedRange, defaultRange));
  }, [selectedRange, selectedMerchant, defaultRange, areDatesEqual]);

  useEffect(() => {
    const isMerchantModified = selectedMerchantForStatus !== "";
    setIsStatusFilterModified(isMerchantModified || !areDatesEqual(selectedRangeForStatus, defaultRange));
  }, [selectedRangeForStatus, selectedMerchantForStatus, defaultRange, areDatesEqual]);

  useEffect(() => {
    getTransactionHistoryByAmountData(
      globalStartDate,
      globalEndDate,
      globalSelectedMerchant,
      globalSelectedWebsite
    );
    getTransactionHistoryByStatusData(
      globalStartDate,
      globalEndDate,
      globalSelectedMerchant,
      globalSelectedWebsite
    );
    fetchTransactionData(globalStartDate, globalEndDate, globalSelectedMerchant);
  }, [
    globalStartDate,
    globalEndDate,
    globalSelectedMerchant,
    globalSelectedWebsite,
    getTransactionHistoryByAmountData,
    getTransactionHistoryByStatusData,
    fetchTransactionData,
  ]);

  // Fetch merchant list on mount
  useEffect(() => {
    getMerchantListData();
  }, [getMerchantListData]);

  useEffect(() => {
    if (
      startDate === globalStartDate &&
      endDate === globalEndDate &&
      selectedMerchant === globalSelectedMerchant
    ) {
      return;
    }
    
    getTransactionHistoryByAmountData(
      startDate,
      endDate,
      selectedMerchant,
      globalSelectedWebsite
    );
  }, [
    startDate,
    endDate,
    selectedMerchant,
    globalSelectedWebsite,
    globalStartDate,
    globalEndDate,
    globalSelectedMerchant,
    getTransactionHistoryByAmountData,
  ]);

  useEffect(() => {
    if (
      startDateForStatus === globalStartDate &&
      endDateForStatus === globalEndDate &&
      selectedMerchantForStatus === globalSelectedMerchant
    ) {
      return;
    }
    
    getTransactionHistoryByStatusData(
      startDateForStatus,
      endDateForStatus,
      selectedMerchantForStatus,
      globalSelectedWebsite
    );
  }, [
    startDateForStatus,
    endDateForStatus,
    selectedMerchantForStatus,
    globalSelectedWebsite,
    globalStartDate,
    globalEndDate,
    globalSelectedMerchant,
    getTransactionHistoryByStatusData,
  ]);


  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExportOption = (type) => {
    handleExportConfirm(type);
    handleMenuClose();
  };

  const handleExportConfirm = async (type) => {
    setExportLoader(true);
    const data = {
      exportType: type,
      startDate: globalStartDate,
      endDate: globalEndDate,
      selectedMerchant,
    };
    try {
      const res = await exportReportDataAction(data);
      if (res.type === "application/json") {
        setSnackbar({
          open: true,
          message: "There is no data exist for export.",
          severity: "info",
        });
      } else {
        const blob = new Blob([res]);
        const timestamp = new Date().getTime();
        let fileName;

        switch (type) {
          case "Excel":
            fileName = `transaction_${timestamp}.xlsx`;
            break;
          case "PDF":
            fileName = `transaction_report_${timestamp}.pdf`;
            break;
          default:
            fileName = `export_${timestamp}.txt`;
        }

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(link.href);
        document.body.removeChild(link);

        setSnackbar({
          open: true,
          message: "Data exported successfully",
          severity: "success",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Something went wrong!! please try again later",
        severity: "error",
      });
    } finally {
      handleMenuClose();
      setExportLoader(false);
    }
  };

  const donutChartData = {
    labels: chartStatusData?.map((item) => item?.transactionStatus) || [],
    datasets: [
      {
        data: chartStatusData?.map((item) => item?.count) || [],
        backgroundColor: ["#344D95", "#ffa726", "#ffca28"],
        borderColor: ["#344D95", "#ffa726", "#ffca28"],
        borderWidth: 1,
      },
    ],
  };

  const donutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
      title: {
        display: true,
        text: "Transaction History By Status",
        font: {
          size: 16,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  const barChartData = {
    labels: chartAmountData?.map((item) => {
      const dateString = item.transactionDate || "";
      return dateString.match(/Week \d+/)?.[0] || dateString;
    }) || [],
    datasets: [
      {
        label: "Total Amount",
        data: chartAmountData?.map((item) => item?.totalAmount) || [],
        backgroundColor: "#ffa726",
        borderColor: "#ffa726",
        borderWidth: 1,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top",
      },
      title: {
        display: true,
        text: "Transaction History By Amount",
        font: {
          size: 16,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const value = context.parsed.y || 0;
            return `Total Amount: ${formatCurrency(value)}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return formatCurrency(value);
          },
        },
      },
    },
  };

  const handleGlobalRangeChange = (range) => {
    if (!range) {
      setGlobalSelectedRange({ from: undefined, to: undefined });
      setGlobalStartDate(null);
      setGlobalEndDate(null);
      return;
    }

    setGlobalSelectedRange(range);

    if (range.from) {
      const year = range.from.getFullYear();
      const month = range.from.getMonth();
      const day = range.from.getDate();
      const startDateObj = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
      setGlobalStartDate(startDateObj.toISOString());
    }

    if (range.to) {
      const year = range.to.getFullYear();
      const month = range.to.getMonth();
      const day = range.to.getDate();
      const endDateObj = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
      setGlobalEndDate(endDateObj.toISOString());
    }

    // Also update the other ranges to match
    setSelectedRange(range);
    if (range.from) {
      const year = range.from.getFullYear();
      const month = range.from.getMonth();
      const day = range.from.getDate();
      const startDateObj = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
      setStartDate(startDateObj.toISOString());
    }
    if (range.to) {
      const year = range.to.getFullYear();
      const month = range.to.getMonth();
      const day = range.to.getDate();
      const endDateObj = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
      setEndDate(endDateObj.toISOString());
    }

    setSelectedRangeForStatus(range);
    if (range.from) {
      const year = range.from.getFullYear();
      const month = range.from.getMonth();
      const day = range.from.getDate();
      const startDateObj = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
      setStartDateForStatus(startDateObj.toISOString());
    }
    if (range.to) {
      const year = range.to.getFullYear();
      const month = range.to.getMonth();
      const day = range.to.getDate();
      const endDateObj = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
      setEndDateForStatus(endDateObj.toISOString());
    }
  };

  const handleAmountRangeChange = (range) => {
    if (!range) {
      setSelectedRange({ from: undefined, to: undefined });
      setStartDate(null);
      setEndDate(null);
      return;
    }

    setSelectedRange(range);

    if (range.from) {
      const year = range.from.getFullYear();
      const month = range.from.getMonth();
      const day = range.from.getDate();
      const startDateObj = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
      setStartDate(startDateObj.toISOString());
    }

    if (range.to) {
      const year = range.to.getFullYear();
      const month = range.to.getMonth();
      const day = range.to.getDate();
      const endDateObj = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
      setEndDate(endDateObj.toISOString());
    }
  };

  const handleStatusRangeChange = (range) => {
    if (!range) {
      setSelectedRangeForStatus({ from: undefined, to: undefined });
      setStartDateForStatus(null);
      setEndDateForStatus(null);
      return;
    }

    setSelectedRangeForStatus(range);

    if (range.from) {
      const year = range.from.getFullYear();
      const month = range.from.getMonth();
      const day = range.from.getDate();
      const startDateObj = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
      setStartDateForStatus(startDateObj.toISOString());
    }

    if (range.to) {
      const year = range.to.getFullYear();
      const month = range.to.getMonth();
      const day = range.to.getDate();
      const endDateObj = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
      setEndDateForStatus(endDateObj.toISOString());
    }
  };

  const handleMerchantChange = (event) => {
    const newMerchantValue =
      event.target.value === "All merchants" ? "" : event.target.value;
    setSelectedMerchant(newMerchantValue);
  };

  const handleMerchantChangeForStatus = (event) => {
    const newMerchantValue =
      event.target.value === "All merchants" ? "" : event.target.value;
    setSelectedMerchantForStatus(newMerchantValue);
  };

  const handleGlobalMerchantChange = (event) => {
    const newMerchantValue =
      event.target.value === "All merchants" ? "" : event.target.value;
    setGlobalSelectedMerchant(newMerchantValue);
    setSelectedMerchant(newMerchantValue);
    setSelectedMerchantForStatus(newMerchantValue);
    setGlobalSelectedWebsite(""); // Reset global website when merchant changes
  };

  const handleResetFiltersForAmount = useCallback(() => {
    const defaultRange = getDefaultDateRange();
    setSelectedRange(defaultRange);
    setStartDate(defaultRange.from.toISOString());
    setEndDate(defaultRange.to.toISOString());
    setSelectedMerchant("");

    getTransactionHistoryByAmountData(
      defaultRange.from.toISOString(),
      defaultRange.to.toISOString(),
      "",
      ""
    );
  }, [getTransactionHistoryByAmountData]);

  const handleResetFiltersForStatus = useCallback(() => {
    const defaultRange = getDefaultDateRange();
    setSelectedRangeForStatus(defaultRange);
    setStartDateForStatus(defaultRange.from.toISOString());
    setEndDateForStatus(defaultRange.to.toISOString());
    setSelectedMerchantForStatus("");

    getTransactionHistoryByStatusData(
      defaultRange.from.toISOString(),
      defaultRange.to.toISOString(),
      "",
      ""
    );
  }, [getTransactionHistoryByStatusData]);

  const handleResetFilters = useCallback(() => {
    const defaultRange = getDefaultDateRange();
    
    setGlobalSelectedRange(defaultRange);
    setGlobalStartDate(defaultRange.from.toISOString());
    setGlobalEndDate(defaultRange.to.toISOString());
    setGlobalSelectedMerchant("");
    setGlobalSelectedWebsite("");
    setSelectedMerchant("");
    setSelectedMerchantForStatus("");

    setSelectedRange(defaultRange);
    setStartDate(defaultRange.from.toISOString());
    setEndDate(defaultRange.to.toISOString());

    setSelectedRangeForStatus(defaultRange);
    setStartDateForStatus(defaultRange.from.toISOString());
    setEndDateForStatus(defaultRange.to.toISOString());

    getTransactionHistoryByAmountData(
      defaultRange.from.toISOString(),
      defaultRange.to.toISOString(),
      "",
      ""
    );
    getTransactionHistoryByStatusData(
      defaultRange.from.toISOString(),
      defaultRange.to.toISOString(),
      "",
      ""
    );
  }, [getTransactionHistoryByAmountData, getTransactionHistoryByStatusData]);

  const TransactionAmountDataGrid = () => {
    const columns = [
      {
        field: "transactionDate",
        headerName: "Date/Period",
        minWidth: 200,
        flex: 1,
        renderCell: (params) => (
          <span>{params.row?.transactionDate || `Week ${params.row?.id}`}</span>
        ),
      },
      {
        field: "totalAmount",
        headerName: "Total Amount",
        minWidth: 150,
        flex: 1,
        renderCell: (params) => (
          <span className="fw-bold text-success">
            {formatCurrency(params.row?.totalAmount) || "-"}
          </span>
        ),
      },
    ];

    const rows =
      chartAmountData
        ?.filter((item) => item.totalAmount > 0)
        ?.map((item, index) => ({
          id: index + 1,
          transactionDate: item.transactionDate || `Week ${index + 1}`,
          totalAmount: item.totalAmount || 0,
        })) || [];

    if (loadingAmount) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 150,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <DataGrid
        className="border-0"
        rows={rows}
        columns={columns}
        autoHeight
        hideFooterRowCount
        hideFooterPagination
        hideFooter
        sx={DataGrid_SX}
        loading={false}
      />
    );
  };

  const TransactionStatusDataGrid = () => {
    const columns = [
      {
        field: "transactionStatus",
        headerName: "Transaction Status",
        minWidth: 180,
        flex: 1,
        renderCell: (params) => {
          const statusMap = {
            success: { text: "Success", status: "success" },
            pending: { text: "Pending", status: "warning" },
            failed: { text: "Failed", status: "error" },
          };

          const statusKey = params.row?.transactionStatus?.toLowerCase() ?? "";

          const { text, status } = statusMap[statusKey] || {
            text: "Unknown",
            status: "default",
          };

          return renderStatusBadge({ status, title: text });
        },
      },
      {
        field: "count",
        headerName: "Count",
        minWidth: 100,
        flex: 1,
        renderCell: (params) => (
          <span className="fw-bold">{params.row?.count || 0}</span>
        ),
      },
      {
        field: "percentage",
        headerName: "Percentage",
        minWidth: 120,
        flex: 1,
        renderCell: (params) => (
          <span className="fw-bold text-primary">
            {params.row?.percentage || 0}%
          </span>
        ),
      },
    ];

    const total =
      chartStatusData?.reduce((sum, data) => sum + data.count, 0) || 1;
    const rows =
      chartStatusData?.map((item, index) => {
        return {
          id: index + 1,
          transactionStatus: item.transactionStatus,
          count: item.count,
          percentage: ((item.count / total) * 100).toFixed(1),
        };
      }) || [];

    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 150,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <DataGrid
        className="border-0"
        rows={rows}
        columns={columns}
        autoHeight
        hideFooterRowCount
        hideFooterPagination
        hideFooter
        sx={DataGrid_SX}
        loading={false}
      />
    );
  };

  const MonthlyReportDataGrid = ({ monthlyData }) => {
    const columns = [
      {
        field: "date",
        headerName: "Date/Period",
        minWidth: 150,
        flex: 1,
        renderCell: (params) => (
          <span>{params.row?.date || params.row?.period || "-"}</span>
        ),
      },
      {
        field: "totalTransactions",
        headerName: "Total Transactions",
        minWidth: 150,
        flex: 1,
        renderCell: (params) => (
          <span className="fw-bold text-info">
            {params.row?.totalTransactions || 0}
          </span>
        ),
      },
      {
        field: "totalAmount",
        headerName: "Total Amount",
        minWidth: 150,
        flex: 1,
        renderCell: (params) => (
          <span className="fw-bold text-success">
            {formatCurrency(params.row?.totalAmount) || "-"}
          </span>
        ),
      },
    ];

    const rows =
      monthlyData?.map((item, index) => {
        return {
          id: index + 1,
          date: item.date || item.period,
          totalTransactions: item.totalTransactions || 0,
          totalAmount: item.totalAmount || 0,
        };
      }) || [];

    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 150,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <DataGrid
        className="border-0"
        rows={rows}
        columns={columns}
        autoHeight
        hideFooterRowCount
        hideFooterPagination
        hideFooter
        sx={DataGrid_SX}
        loading={false}
      />
    );
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <>
      <Grid container spacing={3} style={{}}>
        <Grid item xs={12}>
          <div className="card border-rounded-0 fiter-card">
            <div className="card-body p-5 pb-0">
              <div className="row align-items-center mb-5">
                {/* Title */}
                <div className="col-auto">
                  <h2 className="chart-title mb-0">Global Filter</h2>
                </div>

                {/* Merchant Select */}
                <div className="col-xl-2 col-lg-3 col-md-3 col-sm-6">
                  <FormControl fullWidth>
                    <Select
                      displayEmpty
                      labelId="merchant-select-label"
                      id="merchant-select"
                      value={globalSelectedMerchant || "All merchants"}
                      onChange={handleGlobalMerchantChange}
                      sx={SelectDropdown_SX}
                      MenuProps={MenuProps}
                      input={<OutlinedInput />}
                      disabled={merchantList.length === 0}
                    >
                      <MenuItem value="All merchants">All Tenant</MenuItem>
                      {merchantList?.map((merchant, index) => (
                        <MenuItem key={index} value={merchant.id}>
                          {merchant.companyName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                <div className="col-xl-3 col-lg-4 col-md-4 col-sm-6">
                  <DateRangePicker
                    selectedRange={globalSelectedRange}
                    onRangeChange={handleGlobalRangeChange}
                    maxDate={new Date()}
                    placeholder="Select date range"
                  />
                </div>

                {/* Action Buttons */}
                <div className="col-auto ms-auto d-flex align-items-center gap-2">
                  <CoustomTooltip title="Reset" placement="top" arrow>
                    <span>
                      <button
                        className="btn btn-light text-primary btn-icon"
                        onClick={handleResetFilters}
                        disabled={!isGlobalFilterModified}
                      >
                        <TableReset />
                      </button>
                    </span>
                  </CoustomTooltip>

                  <CoustomTooltip>
                    <Button
                      className="btn btn-primary"
                      aria-controls={isMenuOpen ? "export-menu" : undefined}
                      aria-haspopup="true"
                      onClick={handleExportClick}
                      disabled={exportLoader}
                    >
                      <MyExportIcon className="text-white h-2 w-2 me-1" />{" "}
                      Export
                      {exportLoader && (
                        <span
                          className="spinner-border spinner-border-sm ms-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      )}
                    </Button>
                  </CoustomTooltip>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={isMenuOpen}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={() => handleExportOption("Excel")}>
                      Export as Excel
                    </MenuItem>
                    <MenuItem onClick={() => handleExportOption("PDF")}>
                      Export as PDF
                    </MenuItem>
                  </Menu>
                </div>
              </div>
            </div>
          </div>
        </Grid>
      </Grid>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="transaction report tabs"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                color: "#5E6278",
                "&.Mui-selected": {
                  color: "#3F4254",
                },
              },
            }}
          >
            <Tab label="Table View" />
            <Tab label="Chart View" />
          </Tabs>
        </Box>
        {activeTab === 1 && (
          <Grid container spacing={3} style={{ height: "100%" }}>
            <Grid item xs={12} md={6}>
              <div className="card" style={{ height: "100%" }}>
                <div className="card-body">
                  {/* First row: Date Range */}
                  <div className="row  d-flex  align-items-center pb-10">
                    <div className="col-auto ">
                      <h2 className="chart-title mb-5 ">Transaction Amount</h2>
                    </div>

                    <div className="col-auto mb-5 ms-auto ">
                      <FormControl className="w-100">
                        <Select
                          labelId="merchant-select-label"
                          id="merchant-select"
                          value={selectedMerchant || "All merchants"}
                          label="Select Tenant"
                          onChange={handleMerchantChange}
                          sx={SelectDropdown_SX}
                          MenuProps={MenuProps}
                          input={<OutlinedInput />}
                          disabled={merchantList.length === 0}
                        >
                          <MenuItem value="All merchants">All Tenant</MenuItem>
                          {merchantList?.map((merchant, index) => (
                            <MenuItem key={index} value={merchant.id}>
                              {merchant.companyName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>

                    <div className="d-flex align-items-center mb-5 gap-3 col-auto">
                      <DateRangePicker
                        selectedRange={selectedRange}
                        onRangeChange={handleAmountRangeChange}
                        maxDate={new Date()}
                        placeholder="Select date range"
                      />

                      <CoustomTooltip title="Reset" placement="top" arrow>
                        <span>
                          <button
                            className="btn btn-light text-primary btn-icon"
                            onClick={handleResetFiltersForAmount}
                            disabled={!isAmountFilterModified}
                          >
                            <TableReset />
                          </button>
                        </span>
                      </CoustomTooltip>
                    </div>
                  </div>

                  {/* Chart Section */}
                  <div style={{ height: "350px" }}>
                    {!loadingAmount ? (
                      chartAmountData?.length > 0 ? (
                        <Bar
                          data={barChartData}
                          options={barChartOptions}
                        />
                      ) : (
                        <NoDataFound />
                      )
                    ) : (
                      <Typography align="center">
                        <CircularProgress />
                      </Typography>
                    )}
                  </div>
                </div>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className="card" style={{ height: "100%" }}>
                <div className="card-body">
                  {/* First row: Date Range */}
                  <div className="row align-items-center pb-10">
                    <div className="col-auto ">
                      <h2 className="chart-title mb-5 ">Transaction Status</h2>
                    </div>
                    <div className="col-auto ms-auto">
                      <FormControl className="w-100">
                        <Select
                          labelId="merchant-select-label"
                          id="merchant-select"
                          value={selectedMerchantForStatus || "All merchants"}
                          label="Select Tenant"
                          onChange={handleMerchantChangeForStatus}
                          sx={SelectDropdown_SX}
                          MenuProps={MenuProps}
                          input={<OutlinedInput />}
                          disabled={merchantList.length === 0}
                        >
                          <MenuItem value="All merchants">All Tenant</MenuItem>
                          {merchantList?.map((merchant, index) => (
                            <MenuItem key={index} value={merchant.id}>
                              {merchant.companyName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                    <div className="col-auto d-flex align-items-center mb-5 gap-3">
                      <DateRangePicker
                        selectedRange={selectedRangeForStatus}
                        onRangeChange={handleStatusRangeChange}
                        maxDate={new Date()}
                        placeholder="Select date range"
                      />

                      <CoustomTooltip title="Reset" placement="top" arrow>
                        <span>
                          <button
                            className="btn btn-light text-primary btn-icon"
                            onClick={handleResetFiltersForStatus}
                            disabled={!isStatusFilterModified}
                          >
                            <TableReset />
                          </button>
                        </span>
                      </CoustomTooltip>
                    </div>
                  </div>

                  {/* Chart Section */}
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      height: "350px",
                    }}
                  >
                    {!loading ? (
                      chartStatusData?.length > 0 ? (
                        <Doughnut
                          data={donutChartData}
                          options={donutChartOptions}
                        />
                      ) : (
                        <NoDataFound />
                      )
                    ) : (
                      <Typography align="center">
                        <CircularProgress />
                      </Typography>
                    )}
                  </div>
                </div>
              </div>
            </Grid>

            <Grid item xs={12} md={12}>
              <MonthlyTransactionReportModel
                globalstartDate={globalStartDate}
                globalendDate={globalEndDate}
                globalSelectedMerchant={globalSelectedMerchant}
              />
            </Grid>
          </Grid>
        )}

        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" className="fw-bold mb-4">
                    Transaction Amount Data
                  </Typography>
                  <TransactionAmountDataGrid />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" className="fw-bold mb-4">
                    Transaction Status Data
                  </Typography>
                  <TransactionStatusDataGrid />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" className="fw-bold mb-4">
                    Transaction Report
                  </Typography>
                  <MonthlyReportDataGrid monthlyData={monthlyData} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </>
  );
};

export default TransactionReportModel;